import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

interface IApiEnvelope<T> {
  data: T;
  message?: string;
  meta?: unknown;
}

export interface IIaAlternativa {
  cod?: string | null;
  nm?: string | null;
  score?: number | null;
}

export interface IContaCilLancamento {
  id: number;
  data: string; // YYYY-MM-DD
  historico: string;
  documento?: string | null;
  valor: number;
  dc: 'D' | 'C';
  saldo?: number | null;
  tipo_operacao?: string | null;
  contraparte?: string | null;
  contraparte_cnpj_cpf?: string | null;
  categoria?: string | null;
  debito_poc?: string | null;
  credito_poc?: string | null;
  status: 'conciliado' | 'pendente' | 'revisao';
  confianca?: number | null;
  banco_nome?: string | null;
  conta?: string | null;
  auto_conciliado: boolean;
  fonte_match?: string | null;
  fonte_arquivo?: string | null;
  dt_ia_tentativa?: string | null; // IA já analisou e não conseguiu (anti-loop)
  // Fase 2 — campos extras do model (vêm null até o writer da IA gravar).
  ia_alternativas?: IIaAlternativa[] | null;
  auto_aviso?: string | null;
  origem?: string | null;
  cross_link?: boolean;
  cross_pair_uid?: string | null;
  fontes?: unknown;
  // Extrato de origem tem divergência NÃO discriminada pendente: conciliação
  // bloqueada (o PATCH recusa 409) até o contador resolver o quiz na tela de
  // Extratos. O front mostra chip âmbar e desabilita o select de POC.
  extrato_bloqueado?: boolean;
}

export interface IContaCilLancamentoResumo {
  total: number;
  soma_credito: number;
  soma_debito: number;
  liquido: number;
  periodo_ini?: string | null;
  periodo_fim?: string | null;
  conciliados: number;
  pendentes: number;
  revisao: number;
  // Fase 2 — R$ por status.
  soma_conciliado: number;
  soma_pendente: number;
  soma_revisao: number;
  soma_total: number;
  // Item 1 — composição: conta definitiva (classificado) vs provisória/
  // a-classificar ou ainda não conciliado (provisório).
  qtd_classificado: number;
  soma_classificado: number;
  qtd_provisorio: number;
  soma_provisorio: number;
}

export interface IContaCilExtratoSaldo {
  encontrado: boolean;
  extrato_id?: number | null;
  nome_arquivo?: string | null;
  periodo_ini?: string | null;
  periodo_fim?: string | null;
  saldo_inicial?: number | null;
  saldo_final?: number | null;
  balanco_ok?: boolean | null;
  divergencia_balanco?: number | null;
  anterior_periodo_fim?: string | null;
  anterior_saldo_final?: number | null;
  gap_dias?: number | null;
  continuo?: boolean | null;
}

/**
 * Saldo do extrato de origem de um lançamento (SI/SF) + continuidade com o
 * statement anterior da mesma conta. Só busca com o modal aberto.
 */
export const useContaCilLancamentoExtratoSaldo = (
  lancamentoId: number | null | undefined,
  enabled: boolean,
) =>
  useQuery({
    queryKey: ['contacil', 'lancamento-extrato-saldo', lancamentoId] as const,
    enabled: enabled && lancamentoId != null,
    queryFn: async (): Promise<IContaCilExtratoSaldo> => {
      const res = await api.get<IApiEnvelope<IContaCilExtratoSaldo>>(
        `/api/v1/contacil/lancamentos/${lancamentoId}/extrato-saldo`,
      );
      return res.data.data;
    },
  });

export interface IContaCilQuebraItem {
  chave?: string | null;
  rotulo: string;
  qtd: number;
  soma: number;
  qtd_conciliado: number;
  soma_conciliado: number;
}

export interface IContaCilQuebras {
  por_categoria: IContaCilQuebraItem[];
  por_banco_conta: IContaCilQuebraItem[];
  por_tipo_operacao: IContaCilQuebraItem[];
  por_fonte_match: IContaCilQuebraItem[];
  top_contrapartes: IContaCilQuebraItem[];
}

export interface IContaCilResumoResponse {
  resumo: IContaCilLancamentoResumo;
  quebras: IContaCilQuebras;
}

export interface IContaCilLancamentoPage {
  items: IContaCilLancamento[];
  total: number;
  page: number;
  size: number;
  pages: number;
  resumo: IContaCilLancamentoResumo;
}

export interface IPocOption {
  codigo: string;
  nome: string;
  classificacao?: string | null;
  grupo?: string | null; // nome da sintética-pai (cabeçalho do select A/S)
}

export interface ILancamentosFiltro {
  empresaId?: number;
  status?: string;
  ano?: number;
  mes?: number;
  banco?: string;
  conta?: string;
  search?: string;
  categoria?: string;
  tipoOperacao?: string;
  dc?: string;
  valorMin?: number;
  valorMax?: number;
  semContrapartida?: boolean;
  baixaConfianca?: boolean;
  fonteMatch?: string;
  iaTentou?: boolean; // só os que a IA já tentou (resíduo, pro School AI)
  ordem?: 'recentes' | 'antigos'; // padrão (backend) = antigos (mais antigo -> recente)
  page: number;
  size: number;
}

// Monta os query params compartilhados entre lista e resumo (Fase 2).
const montarParamsFiltro = (filtro: ILancamentosFiltro): Record<string, string | number> => {
  const params: Record<string, string | number> = { empresa_id: filtro.empresaId as number };
  if (filtro.ano) params.ano = filtro.ano;
  if (filtro.mes) params.mes = filtro.mes;
  if (filtro.banco) params.banco = filtro.banco;
  if (filtro.conta) params.conta = filtro.conta;
  if (filtro.search) params.search = filtro.search;
  if (filtro.categoria) params.categoria = filtro.categoria;
  if (filtro.tipoOperacao) params.tipo_operacao = filtro.tipoOperacao;
  if (filtro.dc) params.dc = filtro.dc;
  if (filtro.valorMin != null) params.valor_min = filtro.valorMin;
  if (filtro.valorMax != null) params.valor_max = filtro.valorMax;
  if (filtro.semContrapartida) params.sem_contrapartida = 'true';
  if (filtro.baixaConfianca) params.baixa_confianca = 'true';
  if (filtro.fonteMatch) params.fonte_match = filtro.fonteMatch;
  if (filtro.iaTentou) params.ia_tentou = 'true';
  if (filtro.ordem) params.ordem = filtro.ordem;
  return params;
};

/**
 * Lista paginada de lançamentos de uma empresa (tela Conciliar). Só busca
 * com empresa selecionada. Mantém a página anterior durante o refetch.
 */
export const useContaCilLancamentos = (filtro: ILancamentosFiltro) =>
  useQuery({
    queryKey: ['contacil', 'lancamentos', filtro] as const,
    enabled: filtro.empresaId != null,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<IContaCilLancamentoPage> => {
      const params = montarParamsFiltro(filtro);
      params.page = filtro.page;
      params.size = filtro.size;
      if (filtro.status) params.status = filtro.status;
      const res = await api.get<IApiEnvelope<IContaCilLancamentoPage>>(
        '/api/v1/contacil/lancamentos/',
        { params },
      );
      return res.data.data;
    },
  });

/**
 * Resumo + quebras globais do recorte filtrado (Fase 2). Não pagina; o
 * resumo ignora o filtro de status (mostra o panorama).
 */
export const useContaCilResumo = (filtro: ILancamentosFiltro) =>
  useQuery({
    queryKey: ['contacil', 'lancamentos-resumo', filtro] as const,
    enabled: filtro.empresaId != null,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<IContaCilResumoResponse> => {
      const res = await api.get<IApiEnvelope<IContaCilResumoResponse>>(
        '/api/v1/contacil/lancamentos/resumo',
        { params: montarParamsFiltro(filtro) },
      );
      return res.data.data;
    },
  });

/**
 * POCs analíticas da empresa (folhas que podem receber lançamento) — opções
 * dos selects inline de débito/crédito. Cacheia por 5 min.
 */
export const useContaCilPocs = (empresaId?: number) =>
  useQuery({
    queryKey: ['contacil', 'pocs', empresaId] as const,
    enabled: empresaId != null,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<IPocOption[]> => {
      const res = await api.get<IApiEnvelope<IPocOption[]>>('/api/v1/contacil/lancamentos/pocs', {
        params: { empresa_id: empresaId },
      });
      return res.data.data;
    },
  });

export interface IContaCilNotaSugerida {
  lancamento_id: number;
  tipo: 'entrada' | 'saida';
  numero_nf?: string | null;
  serie?: string | null;
  data?: string | null;
  valor: number;
  contraparte?: string | null;
  match_cnpj?: boolean;
  match_nome?: boolean;
  candidatos: number;
}

/**
 * Notas fiscais candidatas a casar com os lançamentos da página (débito ↔
 * entrada, crédito ↔ saída por empresa + valor + data). Retorna um mapa
 * lancamento_id → sugestão. Uma chamada por página.
 */
export const useContaCilNotasSugeridas = (empresaId: number | undefined, ids: number[]) =>
  useQuery({
    queryKey: ['contacil', 'notas-sugeridas', empresaId, ids] as const,
    enabled: empresaId != null && ids.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<Record<number, IContaCilNotaSugerida>> => {
      const res = await api.get<IApiEnvelope<IContaCilNotaSugerida[]>>(
        '/api/v1/contacil/lancamentos/notas-sugeridas',
        { params: { empresa_id: empresaId, ids: ids.join(',') } },
      );
      const mapa: Record<number, IContaCilNotaSugerida> = {};
      res.data.data.forEach(n => {
        mapa[n.lancamento_id] = n;
      });
      return mapa;
    },
  });

/**
 * Invalida caches que dependem de lancamentos (lista, KPIs, notas sugeridas,
 * saude da empresa). Centralizado pra que cada call-site nao reinvente.
 */
const useInvalidarLancamentos = () => {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['contacil', 'lancamentos'] });
    qc.invalidateQueries({ queryKey: ['contacil', 'lancamentos-resumo'] });
    qc.invalidateQueries({ queryKey: ['contacil', 'notas-sugeridas'] });
    qc.invalidateQueries({ queryKey: ['contacil', 'empresas-saude'] });
  };
};

/**
 * Vincula POC manualmente (PATCH). Enviar `debito_poc`/`credito_poc` como
 * null limpa o vínculo. O backend recalcula o status.
 */
export const useContaCilUpdateLancamento = () => {
  const invalidar = useInvalidarLancamentos();
  return useMutation({
    mutationFn: async (params: {
      id: number;
      debito_poc?: string | null;
      credito_poc?: string | null;
      categoria?: string | null;
    }): Promise<IContaCilLancamento> => {
      const { id, ...body } = params;
      const res = await api.patch<IApiEnvelope<IContaCilLancamento>>(
        `/api/v1/contacil/lancamentos/${id}`,
        body,
      );
      return res.data.data;
    },
    onSuccess: invalidar,
  });
};

export interface IExportarResult {
  filename: string;
  conteudo: string;
  total: number;
  transferencias_dedup: number;
  formato: string;
}

export interface IExportarParams {
  empresaId: number;
  formato: 'dominio' | 'csv';
  dateInit?: string;
  dateEnd?: string;
  banco?: string;
  conta?: string;
}

/** Gera o arquivo de exportação (Domínio TXT ou CSV) sob demanda. */
export const useContaCilExportar = () =>
  useMutation({
    mutationFn: async (p: IExportarParams): Promise<IExportarResult> => {
      const params: Record<string, string | number> = {
        empresa_id: p.empresaId,
        formato: p.formato,
      };
      if (p.dateInit) params.date_init = p.dateInit;
      if (p.dateEnd) params.date_end = p.dateEnd;
      if (p.banco) params.banco = p.banco;
      if (p.conta) params.conta = p.conta;
      const res = await api.get<IApiEnvelope<IExportarResult>>(
        '/api/v1/contacil/lancamentos/exportar',
        { params },
      );
      return res.data.data;
    },
  });

export interface IPeriodoExportavel {
  ano: number;
  mes: number;
  label: string; // 'Dezembro/2025'
  qtd: number;
  qtd_conciliado: number;
  qtd_pendente: number;
  qtd_revisao: number;
  de: string; // YYYY-MM-DD
  ate: string;
  exportavel: boolean; // qtd_conciliado>0 && qtd_pendente===0 && qtd_revisao===0
}

interface IPeriodosExportaveisResponse {
  periodos: IPeriodoExportavel[];
}

/**
 * Lista os meses da empresa onde há lançamentos exportáveis (partida
 * dobrada completa). Usado pelo seletor de período dinâmico do
 * Exportar Domínio. Só faz request quando há empresa selecionada.
 */
export const useContaCilPeriodosExportaveis = (
  empresaId: number | undefined,
  banco?: string | null,
  conta?: string | null,
) =>
  useQuery({
    queryKey: ['contacil', 'periodos-exportaveis', empresaId, banco ?? '', conta ?? ''] as const,
    enabled: empresaId != null,
    queryFn: async (): Promise<IPeriodoExportavel[]> => {
      const params: Record<string, string | number> = { empresa_id: empresaId as number };
      if (banco) params.banco = banco;
      if (conta) params.conta = conta;
      const res = await api.get<IApiEnvelope<IPeriodosExportaveisResponse>>(
        '/api/v1/contacil/lancamentos/periodos-exportaveis',
        { params },
      );
      return res.data.data.periodos;
    },
  });

export type StatusAgg = 'verde' | 'amarelo' | 'vermelho' | 'vazio';

export interface IDiaExportavel {
  dia: number;
  qtd: number;
  qtd_conciliado: number;
  qtd_pendente: number;
  qtd_revisao: number;
  status_agg: StatusAgg;
}

export interface IDiasExportaveisResponse {
  ano_mes: string; // 'YYYY-MM'
  dias: IDiaExportavel[];
  meses_disponiveis: string[]; // ['2025-12', '2026-01', ...]
}

/**
 * Granularidade dia pro calendario heatmap do Exportar Dominio.
 * Retorna dias do mes (com qtd=0/status_agg='vazio' nos dias sem dado)
 * + meses_disponiveis pra setas '<' / '>' do calendario.
 */
export const useContaCilDiasExportaveis = (
  empresaId: number | undefined,
  anoMes: string | undefined,
  banco?: string | null,
  conta?: string | null,
) =>
  useQuery({
    queryKey: [
      'contacil',
      'dias-exportaveis',
      empresaId,
      anoMes,
      banco ?? '',
      conta ?? '',
    ] as const,
    enabled: empresaId != null && !!anoMes,
    // Sem isso o `data` some enquanto o mes novo carrega: as setas do calendario
    // desabilitam, perdem o foco e o Popover (closeOnBlur) fecha.
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<IDiasExportaveisResponse> => {
      const params: Record<string, string | number> = {
        empresa_id: empresaId as number,
        ano_mes: anoMes as string,
      };
      if (banco) params.banco = banco;
      if (conta) params.conta = conta;
      const res = await api.get<IApiEnvelope<IDiasExportaveisResponse>>(
        '/api/v1/contacil/lancamentos/dias-exportaveis',
        { params },
      );
      return res.data.data;
    },
  });
